import React, { useEffect, useRef, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import {
    requestNotificationPermission,
    onMessageListener,
    isNotificationSupported,
    getNotificationPermission,
} from '../utils/FirebaseMessaging';
import {
    shouldAskNotificationPermission,
    incrementPermissionPromptCount,
} from '../utils/Prefs';

const NotificationManager = ({ loggedIn }) => {
    const [notification, setNotification] = useState(null);
    const [showCustomPrompt, setShowCustomPrompt] = useState(false);
    const dismissTimerRef = useRef(null);

    const trySubscribe = useCallback(async () => {
        await requestNotificationPermission();
    }, []);

    useEffect(() => {
        // Only proceed if the user is logged in
        if (!loggedIn) return;
        if (!isNotificationSupported()) return;

        const currentPermission = getNotificationPermission();

        if (currentPermission === 'granted') {
            trySubscribe();
        } else if (shouldAskNotificationPermission()) {
            // Show our custom prompt before triggering the browser dialog
            setShowCustomPrompt(true);
        }
        // If denied or max prompts reached: do nothing
    }, [loggedIn, trySubscribe]);

    useEffect(() => {
        if (!isNotificationSupported()) return;

        const unsubscribe = onMessageListener((payload) => {
            clearTimeout(dismissTimerRef.current);
            setNotification(payload);
            dismissTimerRef.current = setTimeout(() => setNotification(null), 5000);
        });

        return () => {
            if (typeof unsubscribe === 'function') unsubscribe();
            clearTimeout(dismissTimerRef.current);
        };
    }, []);

    const handleAllowNotifications = () => {
        setShowCustomPrompt(false);
        incrementPermissionPromptCount();
        trySubscribe();
    };

    const handleDismissPrompt = () => {
        setShowCustomPrompt(false);
        incrementPermissionPromptCount();
    };

    const closeNotification = () => {
        clearTimeout(dismissTimerRef.current);
        setNotification(null);
    };

    if (!isNotificationSupported()) return null;

    return (
        <div>
            {/* Custom pre-permission prompt */}
            {showCustomPrompt && (
                <div style={styles.promptBanner}>
                    <div style={styles.promptContent}>
                        <strong style={{ fontSize: '15px' }}>Stay updated on transmissions</strong>
                        <p style={{ margin: '6px 0 12px', color: '#444', fontSize: '14px' }}>
                            Get notified when a live satsang transmission begins or when there are
                            important updates — so you never miss a moment.
                        </p>
                        <div style={styles.promptActions}>
                            <button onClick={handleAllowNotifications} style={styles.allowButton}>
                                Enable Notifications
                            </button>
                            <button onClick={handleDismissPrompt} style={styles.dismissButton}>
                                Not now
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Foreground FCM message banner */}
            {notification && (
                <div style={styles.notificationBanner}>
                    <div style={styles.notificationContent}>
                        <strong>{notification.notification?.title || 'Notification'}</strong>
                        <p style={{ margin: '4px 0 0' }}>{notification.notification?.body || ''}</p>
                    </div>
                    <button
                        onClick={closeNotification}
                        style={styles.closeButton}
                        aria-label="Close notification"
                    >
                        ✕
                    </button>
                </div>
            )}
        </div>
    );
};

const styles = {
    promptBanner: {
        position: 'fixed',
        top: '90px',
        right: '20px',
        backgroundColor: '#fff',
        border: '1px solid #ddd',
        borderRadius: '10px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        padding: '20px',
        maxWidth: '360px',
        zIndex: 10000,
    },
    promptContent: {
        display: 'flex',
        flexDirection: 'column',
    },
    promptActions: {
        display: 'flex',
        gap: '10px',
    },
    allowButton: {
        backgroundColor: '#1565c0',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        padding: '8px 16px',
        fontSize: '14px',
        cursor: 'pointer',
        fontWeight: '600',
    },
    dismissButton: {
        backgroundColor: 'transparent',
        color: '#666',
        border: '1px solid #ccc',
        borderRadius: '6px',
        padding: '8px 16px',
        fontSize: '14px',
        cursor: 'pointer',
    },
    notificationBanner: {
        position: 'fixed',
        top: '80px',
        right: '20px',
        backgroundColor: '#fff',
        border: '1px solid #ddd',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        padding: '16px',
        maxWidth: '350px',
        zIndex: 10000,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    notificationContent: {
        flex: 1,
        marginRight: '12px',
    },
    closeButton: {
        background: 'transparent',
        border: 'none',
        fontSize: '20px',
        cursor: 'pointer',
        color: '#666',
        padding: '0',
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
};

const mapStateToProps = ({ session }) => ({
    loggedIn: session.sessionId != null,
});

export default connect(mapStateToProps)(NotificationManager);
